import groupBy from 'lodash.groupby'
import clsx from 'clsx'
import { ColumnsType } from 'antd/lib/table'
import { Icon } from '@iconify/react'
import { Button } from '@pepper/ui'

import CreateItemDropdown from 'components/CreateItemDropdown'
import LoadingBar from 'components/LoadingBar'
import TopicDetailsPopover from 'components/idea-lab/details-popover/TopicDetailsPopover'
import PotentialTrafficTooltipIcon from 'components/idea-lab/PotentialTrafficTooltipIcon'
import GoogleRankPill from 'components/idea-lab/GoogleRankPill'
import TopicActions from '../TopicActions/TopicActions'
import KeywordPill from '../KeywordPill'
import { kFormat } from '../../utils'

import type { Topic } from '@pepper/utils/src/types/seo'
import { KeywordLabel } from 'types/keyword'

import s from './Table.module.css'

type Props = {
  isDomainRemoved: boolean
  projectId: string
  companyId: string
  isLoadingKeywordLabel: boolean
  isGoogleConnected: boolean
  totalRecords: number
  onGetKeywordLabel: (topicSuggestionId: string) => void
  onCreateOrderClick: (topic: Topic) => void
  onCreatePrivateDocClick: (topic: Topic) => void
  onReportClick: (topic: Topic) => void
  onEditTopic: (topic: Topic) => void
  onCreateReport: (topic: Topic) => void
  onDeleteTopic: (topic: Topic) => void
  onViewItemClick: (
    itemId: string,
    itemType: 'private_doc' | 'assignment'
  ) => void
  pageRankingResults?: KeywordLabel
  domain?: string
}

const getIsProcessing = (record: Topic) => {
  return ['pending', 'processing', 'error'].includes(
    record.keyword_generation_status
  )
}

export const getColumns = <RecordType extends Topic>({
  companyId,
  isLoadingKeywordLabel,
  isGoogleConnected,
  totalRecords,
  onGetKeywordLabel,
  onCreateOrderClick,
  onCreatePrivateDocClick,
  onReportClick,
  onViewItemClick,
  onDeleteTopic,
  onEditTopic,
  onCreateReport,
  pageRankingResults,
  domain,
  isDomainRemoved,
}: Props): ColumnsType<RecordType> => {
  return [
    {
      title: () => <div className={s.title}>Topics ({totalRecords})</div>,
      width: '80%',
      render: (_, record, index) => {
        const groupedKeywords = groupBy(record?.keywords, 'type')
        const primaryKeywords = groupedKeywords?.primary ?? []
        const secondaryKeywords = groupedKeywords?.secondary ?? []
        const secondaryKeywordsToShow = secondaryKeywords.slice(0, 2)
        const hiddenKeywordsCount =
          secondaryKeywords.length - secondaryKeywordsToShow.length
        const isReportGenerated =
          record?.crr?.generation_status_name === 'completed'

        return (
          <>
            <div className={s.wrapper}>
              {primaryKeywords.length > 0 ? (
                <TopicDetailsPopover
                  isLoading={isLoadingKeywordLabel}
                  onGetKeywordLabel={() =>
                    onGetKeywordLabel(primaryKeywords?.[0]?.id)
                  }
                  keywordLabel={pageRankingResults}
                  topSearchResults={pageRankingResults?.top_ranking_details}
                  selfRankingPages={pageRankingResults?.self_ranking_pages}
                  domain={domain}
                  isGoogleConnected={isGoogleConnected}
                  companyId={companyId}
                  secondaryKeywords={secondaryKeywords}
                  topic={record}
                  primaryKeyword={primaryKeywords[0]?.keyword}
                  updatedAt={pageRankingResults?.updated_at}
                  isDomainRemoved={isDomainRemoved}
                >
                  <div className={s.nameWrapper}>
                    <div className={s.name}>{record.name}</div>
                    <Icon
                      icon="lucide:chevrons-right"
                      className={s.popoverIcon}
                      height={20}
                      width={20}
                    />
                  </div>
                </TopicDetailsPopover>
              ) : (
                <div className={s.nameWrapper}>
                  <div className={s.name}>{record.name}</div>
                </div>
              )}
              {isReportGenerated && (
                <Button
                  className={s.btn}
                  type="link"
                  size="small"
                  icon={<Icon icon="lucide:file-text" />}
                  onClick={() => onReportClick(record)}
                  data-id={`topic-${index + 1}-active-seo-guidelines-button`}
                >
                  SEO Guidelines
                </Button>
              )}
              <TopicActions
                className={s.topicActions}
                topic={record}
                onEditTopic={onEditTopic}
                dataIdIndex={index + 1}
                onGeneratingReportClick={() => onReportClick(record)}
                onCreateReport={onCreateReport}
              />
            </div>
            <div className={s.keywordsWrapper}>
              {getIsProcessing(record) && primaryKeywords.length === 0 ? (
                <LoadingBar width={110} height={20} borderRadius={50} />
              ) : (
                primaryKeywords.map(keyword => (
                  <KeywordPill
                    key={keyword.id}
                    keyword={keyword.keyword}
                    volume={keyword.volume}
                    isPrimary={true}
                  />
                ))
              )}
              {getIsProcessing(record) &&
              secondaryKeywordsToShow.length === 0 ? (
                <LoadingBar width={110} height={20} borderRadius={50} />
              ) : (
                secondaryKeywordsToShow.map(keyword => (
                  <KeywordPill
                    key={keyword.id}
                    keyword={keyword.keyword}
                    volume={keyword.volume}
                  />
                ))
              )}
              {hiddenKeywordsCount > 0 && (
                <TopicDetailsPopover
                  isLoading={isLoadingKeywordLabel}
                  onGetKeywordLabel={() =>
                    onGetKeywordLabel(primaryKeywords?.[0]?.id)
                  }
                  keywordLabel={pageRankingResults}
                  topSearchResults={pageRankingResults?.top_ranking_details}
                  selfRankingPages={pageRankingResults?.self_ranking_pages}
                  domain={domain}
                  isGoogleConnected={isGoogleConnected}
                  companyId={companyId}
                  secondaryKeywords={secondaryKeywords}
                  topic={record}
                  primaryKeyword={primaryKeywords[0]?.keyword}
                  updatedAt={pageRankingResults?.updated_at}
                  isDomainRemoved={isDomainRemoved}
                >
                  <KeywordPill keyword={`+${hiddenKeywordsCount}`} />
                </TopicDetailsPopover>
              )}
            </div>
            <div className={s.rankWrapper}>
              {primaryKeywords?.[0]?.self_top_position && (
                <GoogleRankPill
                  googleRank={primaryKeywords[0].self_top_position}
                />
              )}
              {primaryKeywords?.[0]?.self_top_topic && (
                <div
                  className={s.rankedTopic}
                  title={primaryKeywords[0].self_top_topic}
                >
                  {primaryKeywords[0].self_top_topic}
                </div>
              )}
            </div>
          </>
        )
      },
    },
    {
      title: () => (
        <div className={s.title}>
          Potential Traffic
          <PotentialTrafficTooltipIcon />
        </div>
      ),
      width: 160,
      render: (_, record) => {
        if (record.expected_traffic_start && record.expected_traffic_end) {
          return (
            <div className={clsx(s.numbers, s.entry)}>
              {`${kFormat(record.expected_traffic_start)} - ${kFormat(
                record.expected_traffic_end
              )}`}
              <Icon
                icon="uil:arrow-growth"
                className={s.icon}
                height={16}
                width={16}
              />
            </div>
          )
        }

        return getIsProcessing(record) ? (
          <LoadingBar width={110} height={20} borderRadius={50} />
        ) : (
          'NA'
        )
      },
    },
    {
      title: () => <div className={clsx(s.title, s.action)}>Action</div>,
      render: (_, record, index) => {
        const isMarketplaceDisabled =
          record?.crr &&
          ['pending', 'processing'].includes(record.crr.generation_status_name)

        return record?.item_id ? (
          <Button
            type="link"
            onClick={() => onViewItemClick(record.item_id!, record.item_type!)}
            icon={<Icon icon="lucide:arrow-up-right" />}
          >
            View Item
          </Button>
        ) : (
          <CreateItemDropdown
            isMarketplaceDisabled={isMarketplaceDisabled}
            onCreateOrderClick={() => onCreateOrderClick(record)}
            onCreatePrivateDocClick={() => onCreatePrivateDocClick(record)}
          >
            <Button
              className={s.createButton}
              type="link"
              icon={<Icon icon="lucide:plus" />}
              data-id={`topic-${index + 1}-create-item-button`}
            >
              Create Item
            </Button>
          </CreateItemDropdown>
        )
      },
    },
    {
      title: '',
      render: (_, record) => {
        const isGeneratingSeoGuidelines =
          record?.crr &&
          ['pending', 'processing'].includes(record.crr.generation_status_name)

        const isDisabled =
          (!!record?.crr && !!record?.item_id) || isGeneratingSeoGuidelines

        return (
          <Button
            type="text"
            size="small"
            icon={<Icon icon="lucide:trash-2" />}
            onClick={() => onDeleteTopic(record)}
            disabled={isDisabled}
            className={s.deleteBtn}
            {...(isGeneratingSeoGuidelines && {
              title: 'Cannot perform actions while generating SEO Guideline',
            })}
          />
        )
      },
    },
  ]
}
